
import Vue, { PropType } from 'vue';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useColorManagementStore } from '@/stores/v2/colorsManagement';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { mapActions, mapState, mapStores } from 'pinia';
import { CvButton } from '@carbon/vue';
import Close16 from '@carbon/icons-vue/es/close/16';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';
import { ProductPrintPositionAddModel } from '@/typings';
import { ColorDropdown } from '@/web-components/import-wizard-v2/components/ColorManagement/ColorDropdown';
import { createStateSnapshot } from '@/utils/historyHelperV2';
import { PmsColor } from '@midocean/webapps-shared-domain';
import { TextShape } from '@/web-components/import-wizard/typings/pintura';

export default Vue.extend({
  name: 'ColorManagement',
  components: {
    CvButton,
    Close16,
    ColorDropdown,
  },
  props: {
    isDisabled: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    ...mapStores(useShapeStore, useColorManagementStore),
    ...mapState(useProductStore, [
      'data',
      'isLaserConverter',
      'isDebossingTechnique',
      'unavailablePrintPositions',
      'selectedVariant',
      'selectedPrintPosition',
      'isFullColor',
    ]),
    ...mapState(useColorManagementStore, ['availablePmsColors']),
    ...mapState(useShapeStore, [
      'shapes',
      'positionShapeMap',
      'positionShapes',
    ]),
    allPmsColors(): PmsColor[] {
      const detected = this.getPmsColorsOnPrintPosition();

      const availableWithoutDetected = this.availablePmsColors.filter((c) => {
        return detected.findIndex((dc) => dc.name === c.name) === -1;
      });

      const availableDetected = this.availablePmsColors.filter((c) => {
        return detected.findIndex((dc) => dc.name === c.name) !== -1;
      });

      // show detected colors first, then the rest of available pms colors
      const mergedList = availableDetected.concat(availableWithoutDetected);

      return mergedList;
    },

    isPantoneTechnique(): boolean {
      return (
        !this.isFullColor() &&
        !this.isLaserConverter() &&
        !this.isDebossingTechnique()
      );
    },
  },

  methods: {
    ...mapActions(useColorManagementStore, ['getPmsColorsOnPrintPosition']),
    ...mapActions(useShapeStore, ['replaceColorV2', 'removeColorV2']),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useMirrorStore, ['updateMirrorShape']),
    ...mapActions(useNotificationStore, ['runValidators']),
    ...mapActions(useGlobalStore, ['setIsLoaderActive']),

    async replaceWith(index: number, color: string) {
      if (this.selectedPrintPosition.id) {
        this.setIsLoaderActive(true);
        if (this.getPmsColorsOnPrintPosition()[index].name === color) {
          // if the same color is selected, do nothing.
          return;
        }

        const historyStore = useHistoryStore();

        const selectedPmsColor =
          this.availablePmsColors?.find((c) => {
            return c.name === color;
          }) || ({} as PmsColor);

        // Use V2 color engine flow
        await this.replaceColorV2(
          this.getPmsColorsOnPrintPosition()[index],
          selectedPmsColor
        );

        const eventData = this.unavailablePrintPositions.filter(
          (u) => u.positionId === this.selectedPrintPosition.id
        ) as ProductPrintPositionAddModel[];

        const eventPayload = generateEventPayloadV2();

        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });

        this.$emit('action:change-color', eventData);

        const positionShapes = this.positionShapes(
          this.selectedPrintPosition.id
        );
        positionShapes.forEach((x) => {
          this.updateMirrorShape(x);
        });

        const snapshot = createStateSnapshot();
        historyStore.recordHistory(snapshot);

        this.runValidators();

        this.setIsLoaderActive(false);
      }
    },

    async colorToRemove(color: PmsColor) {
      if (this.selectedPrintPosition.id) {
        this.setIsLoaderActive(true);
        // Use V2 color engine flow
        await this.removeColorV2(color);

        const eventData = this.unavailablePrintPositions.filter(
          (u) => u.positionId === this.selectedPrintPosition.id
        ) as ProductPrintPositionAddModel[];

        this.$emit('action:change-color', eventData);

        const eventPayload = generateEventPayloadV2();

        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });

        this.runValidators();

        const snapshot = createStateSnapshot();
        this.recordHistory(snapshot);

        this.setIsLoaderActive(false);
      }
    },
  },
});
